import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

interface Prospect {
  id: string;
  enseigne: string;
  date_visite: string;
  mall: string;
  contact: string;
  email: string;
  telephone: string;
  synthese: string;
  action_suivi: string;
  date_action: string;
  photo_url?: string;
}

export default function CRM() {
  const [form, setForm] = useState({
    enseigne: "",
    date_visite: "",
    mall: "",
    contact: "",
    email: "",
    telephone: "",
    synthese: "",
    action_suivi: "",
    date_action: "",
    photo: null as File | null,
  });
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  async function loadProspects() {
    const { data, error } = await supabase
      .from("prospects")
      .select("*")
      .order("date_action", { ascending: true });
    if (!error) setProspects(data || []);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    let photo_url: string | null = null;
    if (form.photo) {
      const fileName = `${Date.now()}_${form.photo.name}`;
      const { error: uploadError } = await supabase.storage
        .from("prospect-photos")
        .upload(fileName, form.photo, { upsert: true });
      if (uploadError) {
        alert("Erreur upload photo : " + uploadError.message);
        return;
      }
      photo_url = supabase.storage
        .from("prospect-photos")
        .getPublicUrl(fileName).data.publicUrl;
    }

    if (editingId) {
      // 🔄 Update
      const { error } = await supabase
        .from("prospects")
        .update({
          enseigne: form.enseigne,
          date_visite: form.date_visite,
          mall: form.mall,
          contact: form.contact,
          email: form.email,
          telephone: form.telephone,
          synthese: form.synthese,
          action_suivi: form.action_suivi,
          date_action: form.date_action,
          ...(photo_url ? { photo_url } : {}),
        })
        .eq("id", editingId);

      if (error) alert("Erreur update : " + error.message);
      else setEditingId(null);
    } else {
      // ➕ Insertion
      const { error } = await supabase.from("prospects").insert([
        {
          enseigne: form.enseigne,
          date_visite: form.date_visite,
          mall: form.mall,
          contact: form.contact,
          email: form.email,
          telephone: form.telephone,
          synthese: form.synthese,
          action_suivi: form.action_suivi,
          date_action: form.date_action,
          photo_url,
        },
      ]);
      if (error) alert("Erreur insert : " + error.message);
    }

    setForm({
      enseigne: "",
      date_visite: "",
      mall: "",
      contact: "",
      email: "",
      telephone: "",
      synthese: "",
      action_suivi: "",
      date_action: "",
      photo: null,
    });
    loadProspects();
  }

  async function handleDelete(id: string) {
    if (!window.confirm("Supprimer ce prospect ?")) return;
    const { error } = await supabase.from("prospects").delete().eq("id", id);
    if (error) alert("Erreur suppression : " + error.message);
    else loadProspects();
  }

  useEffect(() => {
    loadProspects();
  }, []);

  return (
    <div className="space-y-8">
      {/* Formulaire */}
      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white p-6 rounded-lg shadow"
      >
        <input
          type="text"
          placeholder="Nom de l'enseigne"
          value={form.enseigne}
          onChange={(e) => setForm({ ...form, enseigne: e.target.value })}
          className="border p-2 rounded"
          required
        />
        <input
          type="date"
          value={form.date_visite}
          onChange={(e) => setForm({ ...form, date_visite: e.target.value })}
          className="border p-2 rounded"
          required
        />
        <input
          type="text"
          placeholder="Mall visité"
          value={form.mall}
          onChange={(e) => setForm({ ...form, mall: e.target.value })}
          className="border p-2 rounded"
        />
        <input
          type="text"
          placeholder="Contact"
          value={form.contact}
          onChange={(e) => setForm({ ...form, contact: e.target.value })}
          className="border p-2 rounded"
        />
        <input
          type="email"
          placeholder="Email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="border p-2 rounded"
        />
        <input
          type="tel"
          placeholder="Téléphone"
          value={form.telephone}
          onChange={(e) => setForm({ ...form, telephone: e.target.value })}
          className="border p-2 rounded"
        />
        <textarea
          placeholder="Synthèse de la visite"
          value={form.synthese}
          onChange={(e) => setForm({ ...form, synthese: e.target.value })}
          className="border p-2 rounded md:col-span-2"
        />
        <input
          type="text"
          placeholder="Action de suivi"
          value={form.action_suivi}
          onChange={(e) => setForm({ ...form, action_suivi: e.target.value })}
          className="border p-2 rounded"
        />
        <input
          type="date"
          value={form.date_action}
          onChange={(e) => setForm({ ...form, date_action: e.target.value })}
          className="border p-2 rounded"
        />
        <input
          type="file"
          accept="image/*"
          onChange={(e) =>
            setForm({ ...form, photo: e.target.files ? e.target.files[0] : null })
          }
          className="md:col-span-2"
        />
        <button
          type="submit"
          className="bg-primary text-white px-4 py-2 rounded col-span-1 md:col-span-2"
        >
          {editingId ? "Mettre à jour le prospect" : "Ajouter prospect"}
        </button>
      </form>

      {/* Résumé du pipe */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-bold mb-4">Actions à réaliser</h2>
        <ul className="space-y-2">
          {prospects.map((p) => (
            <li key={p.id} className="border rounded p-3">
              <div className="flex justify-between items-center">
                <div>
                  <div className="font-semibold">{p.enseigne}</div>
                  <div className="text-sm text-gray-600">
                    {p.action_suivi} — {p.date_action || "pas de date"}
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <button
                    className="text-blue-600 text-sm"
                    onClick={() => {
                      setEditingId(p.id);
                      setForm({
                        enseigne: p.enseigne || "",
                        date_visite: p.date_visite || "",
                        mall: p.mall || "",
                        contact: p.contact || "",
                        email: p.email || "",
                        telephone: p.telephone || "",
                        synthese: p.synthese || "",
                        action_suivi: p.action_suivi || "",
                        date_action: p.date_action || "",
                        photo: null,
                      });
                    }}
                  >
                    ✏️ Modifier
                  </button>
                  <button
                    className="text-red-600 text-sm"
                    onClick={() => handleDelete(p.id)}
                  >
                    🗑️ Supprimer
                  </button>
                  <button
                    className="text-primary font-bold text-lg"
                    onClick={() => setOpenId(openId === p.id ? null : p.id)}
                  >
                    {openId === p.id ? "−" : "+"}
                  </button>
                </div>
              </div>

              {openId === p.id && (
                <div className="mt-3 text-sm text-gray-700 space-y-1">
                  <div><strong>Visite :</strong> {p.date_visite} ({p.mall})</div>
                  <div><strong>Contact :</strong> {p.contact}</div>
                  {p.email && <div><strong>Email :</strong> {p.email}</div>}
                  {p.telephone && <div><strong>Téléphone :</strong> {p.telephone}</div>}
                  <div><strong>Synthèse :</strong> {p.synthese}</div>
                  {p.photo_url && (
                    <div>
                      <img
                        src={p.photo_url}
                        alt="Carte de visite"
                        className="w-40 rounded mt-2 border"
                      />
                    </div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
